import { ColDef, ICellRendererParams } from 'ag-grid-community';
import styles from './UploadHistoryDrawer.module.scss';
import { Button, Typography } from '@navi/web-ui/lib/primitives';
import Download from '@cp/src/assets/images/icons/Download';
import LoaderIcon from '@cp/src/assets/icons/LoaderIcon';
import ErrorIconOutline from '@cp/src/assets/icons/ErrorIconOutline';
import { useMemo } from 'react';
import { FILE_STATUS } from '../../AmeyoUtility/types';
import {
  fetchFailureReportFile,
  fetchFile,
  fetchNdraFailureFile
} from './actions/uploadHistoryDrawerActions';
import dayjs from 'dayjs';
import { DateTimeFormat } from '@cp/src/components/DateTimePicker/constants';
import { BulkUploadFileType, errorActionType, successActionType } from './types.ts/types';
import { Tooltip, TooltipContent, TooltipTrigger } from '@cp/src/components/TooltipV2/TooltipV2';
import cx from 'classnames';

export const useColumnDefs = (): {
  columnDefs: ColDef[];
  handleFileButtonClick: (fileReferenceId: string, status: string) => void;
} => {
  const handleFileButtonClick = (fileReferenceId: string, status: string) => {
    if (status === FILE_STATUS.COMPLETED || status === FILE_STATUS.FAILED) {
      fetchFile(fileReferenceId);
    }
  };

  const handleFailureReportButtonClick = (fileReferenceId: string, type: string) => {
    if (type === BulkUploadFileType.UPLOAD_NDRA_CERTIFICATE) {
      fetchNdraFailureFile(fileReferenceId);
      return;
    }
    fetchFailureReportFile(fileReferenceId);
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: 'type',
        headerName: 'Action type',
        wrapHeaderText: true,
        minWidth: 130,
        maxWidth: 180,
        wrapText: true,
        headerComponentParams: { width: 180 },
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          return data?.status !== FILE_STATUS.FAILED ? (
            <>
              <div>
                <Typography variant="p4" color="var(--grayscale-2)">
                  {successActionType[data?.type]}
                </Typography>
              </div>
            </>
          ) : (
            <>
              <div>
                <div className={'flex'}>
                  <Typography variant="p4" color="var(--navi-color-red-base)">
                    {errorActionType[data?.type]}
                  </Typography>
                  <div className="pl-[2px] pt-[2px]">
                    <ErrorIconOutline />
                  </div>
                </div>
              </div>
            </>
          );
        }
      },
      {
        field: 'successCount',
        headerName: 'Uploaded',
        minWidth: 80,
        maxWidth: 90,
        headerComponentParams: { width: 100 },
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          if (
            data?.status === FILE_STATUS.INITIATED ||
            data?.status === FILE_STATUS.FAILED ||
            data?.status === FILE_STATUS.INPROGRESS
          ) {
            return '-';
          }
          return <span>{data?.successCount}</span>;
        }
      },
      {
        field: 'failureCount',
        headerName: 'Failed',
        minWidth: 50,
        maxWidth: 70,
        headerComponentParams: { width: 100 },
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          if (
            data?.status === FILE_STATUS.INITIATED ||
            data?.status === FILE_STATUS.FAILED ||
            data?.status === FILE_STATUS.INPROGRESS
          ) {
            return '-';
          }
          const textColor = data?.failureCount ? 'var(--red-base)' : 'var(--grayscale-2)';
          return <span style={{ color: textColor }}>{data?.failureCount}</span>;
        }
      },
      {
        field: 'uploadDateTime',
        headerName: 'Upload Date & Time',
        minWidth: 150,
        maxWidth: 200,
        wrapHeaderText: false,
        headerComponentParams: { width: 150 },
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          const textColor = 'var(--grayscale-3';
          return (
            <span style={{ color: textColor }}>
              {' '}
              {dayjs(data?.startTime).utc(data?.startTime).local().format(DateTimeFormat.DATE_TIME)}
            </span>
          );
        }
      },
      {
        field: 'originalFile',
        headerName: 'Original File',
        minWidth: 100,
        maxWidth: 130,
        headerComponentParams: { width: 100 },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          const textColor =
            data?.status === (FILE_STATUS.INPROGRESS || data?.status === FILE_STATUS.INITIATED)
              ? 'var(--green-base)'
              : 'var(--blue-base)';
          const icon =
            data?.status === FILE_STATUS.COMPLETED || data?.status === FILE_STATUS.FAILED ? (
              <Download />
            ) : (
              <LoaderIcon
                size={20}
                borderWidth={3}
                borderColor="var(--greyscale-light)"
                fillColor="var(--green-base)"
              />
            );
          const cursorStyle =
            data?.status === FILE_STATUS.INPROGRESS || data?.status === FILE_STATUS.INITIATED
              ? 'auto'
              : 'pointer';
          return data?.originalFileExists ? (
            <Button
              variant="text"
              className={styles.buttonWrapper}
              style={{ color: textColor, cursor: cursorStyle }}
              onClick={() => handleFileButtonClick(data?.referenceId, data?.status)}
              startAdornment={data?.status && icon}
            >
              <Tooltip placement="top" hideStrategy="referenceHidden">
                <TooltipTrigger>
                  <div
                    className={cx(
                      styles.buttonText,
                      data?.originalFileName ? 'truncate block w-[80px]' : ''
                    )}
                  >
                    {data?.status && (data?.status === 'COMPLETED' || data?.status === 'FAILED')
                      ? `${data?.originalFileName || 'Download'}`
                      : `${data?.originalFileName || 'In Progress'}`}
                  </div>
                </TooltipTrigger>
                {data?.originalFileName && (
                  <TooltipContent className={'tooltipWrapper'}>
                    {data?.originalFileName}
                  </TooltipContent>
                )}
              </Tooltip>
            </Button>
          ) : (
            <Typography variant="p4" color="var(--grayscale-2)">
              -
            </Typography>
          );
        }
      },
      {
        field: 'failureReport',
        headerName: 'Failure Report',
        wrapHeaderText: false,
        minWidth: 100,
        maxWidth: 130,
        headerComponentParams: { width: 80 },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          return data?.failureCount > 0 && data?.status === FILE_STATUS.COMPLETED ? (
            <Button
              variant="text"
              className={styles.buttonWrapper}
              onClick={() => handleFailureReportButtonClick(data?.referenceId, data?.type)}
              startAdornment={<Download />}
            >
              <span className={styles.buttonText}>Download</span>
            </Button>
          ) : (
            <Typography variant="p4" color="var(--grayscale-2)">
              -
            </Typography>
          );
        }
      }
    ],
    []
  );
  return { columnDefs, handleFileButtonClick };
};

export default useColumnDefs;
